import { useEffect, useState } from "react";
import api, { formatIDR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Beef, Utensils, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const empty = {
  name: "", category: "hewan",
  price: "", price_male: "", price_female: "", box_tier: "hemat",
  price_male_cooked: "", price_female_cooked: "",
  cost_price: "", cost_male: "", cost_female: "", cook_cost: "",
  description: "",
};

const TIER_LABEL = { hemat: "Hemat", standar: "Standar", spesial: "Spesial" };

export default function Packages() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/packages").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = (category) => { setEditing(null); setForm({ ...empty, category }); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...empty, ...p,
      category: p.category || "hewan",
      price: p.price != null ? String(p.price) : "",
      price_male: p.price_male != null ? String(p.price_male) : "",
      price_female: p.price_female != null ? String(p.price_female) : "",
      price_male_cooked: p.price_male_cooked != null ? String(p.price_male_cooked) : "",
      price_female_cooked: p.price_female_cooked != null ? String(p.price_female_cooked) : "",
      cost_price: p.cost_price != null ? String(p.cost_price) : "",
      cost_male: p.cost_male != null ? String(p.cost_male) : "",
      cost_female: p.cost_female != null ? String(p.cost_female) : "",
      cook_cost: p.cook_cost != null ? String(p.cook_cost) : "",
      box_tier: p.box_tier || "hemat",
    });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const isBox = form.category === "nasi_box";
      const isAddon = form.category === "menu_tambahan";
      const payload = {
        name: form.name,
        category: form.category,
        type: isBox || isAddon ? "" : "domba",
        box_tier: isBox ? form.box_tier : "",
        price: Number(isBox || isAddon ? form.price : form.price_male) || 0,
        price_male: Number(form.price_male) || 0,
        price_female: Number(form.price_female) || 0,
        price_male_cooked: Number(form.price_male_cooked) || 0,
        price_female_cooked: Number(form.price_female_cooked) || 0,
        cost_price: Number(form.cost_price) || 0,
        cost_male: Number(form.cost_male) || 0,
        cost_female: Number(form.cost_female) || 0,
        cook_cost: isBox ? 0 : Number(form.cook_cost) || 0,
        portions: 0,
        description: form.description,
        active: true,
      };
      if (editing) {
        await api.put(`/packages/${editing.id}`, payload);
        toast.success("Data harga diperbarui");
      } else {
        await api.post("/packages", payload);
        toast.success("Data harga ditambahkan");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/packages/${id}`);
      toast.success("Data harga dihapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const hewan = items.filter((p) => (p.category || "hewan") === "hewan")
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "id", { numeric: true }));
  const boxes = items.filter((p) => p.category === "nasi_box");
  const addons = items.filter((p) => p.category === "menu_tambahan");
  const isBox = form.category === "nasi_box";
  const isAddon = form.category === "menu_tambahan";

  const Card = ({ p, i }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: i * 0.04 }}
      className="flex flex-col rounded-2xl border border-brand-line bg-white p-6 shadow-[0_8px_30px_rgba(45,35,30,0.05)]"
      data-testid={`package-card-${p.id}`}
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-gold/15 text-brand-gold">
        {p.category === "nasi_box" ? <Utensils className="h-5 w-5" /> : p.category === "menu_tambahan" ? <PlusCircle className="h-5 w-5" /> : <Beef className="h-5 w-5" />}
      </div>
      <h3 className="mt-4 font-serif text-xl font-bold text-brand-espresso">{p.name}</h3>
      {p.category === "nasi_box" && (
        <p className="mt-1 text-xs uppercase tracking-wide text-brand-gold">{TIER_LABEL[p.box_tier] || p.box_tier}</p>
      )}
      {p.description && <p className="mt-2 flex-1 text-sm text-brand-muted">{p.description}</p>}

      {p.category === "nasi_box" ? (
        <p className="mt-4 font-serif text-2xl font-bold text-brand-espresso">{formatIDR(p.price)}<span className="text-sm font-sans font-normal text-brand-muted"> /box</span></p>
      ) : p.category === "menu_tambahan" ? (
        <p className="mt-4 font-serif text-2xl font-bold text-brand-espresso">{formatIDR(p.price)}</p>
      ) : (
        <div className="mt-4 space-y-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-brand-gold">Harga Jual Mentah</p>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-brand-surface p-2.5"><p className="text-[11px] uppercase tracking-wide text-brand-muted">Jantan</p><p className="font-serif text-base font-bold text-brand-espresso">{formatIDR(p.price_male)}</p></div>
              <div className="rounded-lg bg-brand-surface p-2.5"><p className="text-[11px] uppercase tracking-wide text-brand-muted">Betina</p><p className="font-serif text-base font-bold text-brand-espresso">{formatIDR(p.price_female)}</p></div>
            </div>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-brand-gold">Harga Jual Masak</p>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-brand-surface p-2.5"><p className="text-[11px] uppercase tracking-wide text-brand-muted">Jantan</p><p className="font-serif text-base font-bold text-brand-espresso">{formatIDR(p.price_male_cooked)}</p></div>
              <div className="rounded-lg bg-brand-surface p-2.5"><p className="text-[11px] uppercase tracking-wide text-brand-muted">Betina</p><p className="font-serif text-base font-bold text-brand-espresso">{formatIDR(p.price_female_cooked)}</p></div>
            </div>
          </div>
        </div>
      )}

      {isAdmin && (
        <p className="mt-2 text-xs text-brand-muted" data-testid={`package-modal-${p.id}`}>
          {p.category === "nasi_box"
            ? `Modal: ${formatIDR(p.cost_price)}/box`
            : p.category === "menu_tambahan"
            ? `Modal: ${formatIDR(p.cost_price)}`
            : `Modal J: ${formatIDR(p.cost_male)} · B: ${formatIDR(p.cost_female)} + masak ${formatIDR(p.cook_cost)}`}
        </p>
      )}

      {isAdmin && (
        <div className="mt-4 flex gap-2">
          <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`edit-package-${p.id}`} className="flex-1 gap-1.5 border-brand-line">
            <Pencil className="h-3.5 w-3.5" /> Ubah
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid={`delete-package-${p.id}`} className="border-brand-line text-destructive hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Hapus data ini?</AlertDialogTitle>
                <AlertDialogDescription>"{p.name}" akan dihapus permanen.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Batal</AlertDialogCancel>
                <AlertDialogAction onClick={() => remove(p.id)} className="bg-destructive hover:bg-destructive/90">Hapus</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </motion.div>
  );

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8 animate-fade-up">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-gold">Katalog</p>
        <h1 className="mt-1 font-serif text-4xl font-bold text-brand-espresso">Daftar Harga</h1>
      </div>

      {/* Hewan */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Beef className="h-5 w-5 text-brand-gold" />
          <h2 className="font-serif text-2xl font-bold text-brand-espresso">Hewan Aqiqah</h2>
        </div>
        {isAdmin && (
          <Button onClick={() => openNew("hewan")} data-testid="add-package-btn"
            className="gap-2 bg-brand-gold text-brand-espresso hover:bg-brand-goldhover transition-colors duration-200">
            <Plus className="h-4 w-4" /> Tambah Domba
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {hewan.length === 0 && <div className="col-span-full rounded-2xl border border-dashed border-brand-line bg-white p-8 text-center text-brand-muted">Belum ada data domba.</div>}
        {hewan.map((p, i) => <Card key={p.id} p={p} i={i} />)}
      </div>

      {/* Nasi box */}
      <div className="mb-4 mt-10 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Utensils className="h-5 w-5 text-brand-gold" />
          <h2 className="font-serif text-2xl font-bold text-brand-espresso">Nasi Box</h2>
        </div>
        {isAdmin && (
          <Button variant="outline" onClick={() => openNew("nasi_box")} data-testid="add-box-btn"
            className="gap-2 border-brand-line text-brand-espresso hover:bg-brand-surface transition-colors duration-200">
            <Plus className="h-4 w-4" /> Tambah Nasi Box
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {boxes.length === 0 && <div className="col-span-full rounded-2xl border border-dashed border-brand-line bg-white p-8 text-center text-brand-muted">Belum ada nasi box.</div>}
        {boxes.map((p, i) => <Card key={p.id} p={p} i={i} />)}
      </div>

      {/* Menu Tambahan */}
      <div className="mb-4 mt-10 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <PlusCircle className="h-5 w-5 text-brand-gold" />
          <h2 className="font-serif text-2xl font-bold text-brand-espresso">Add On</h2>
        </div>
        {isAdmin && (
          <Button variant="outline" onClick={() => openNew("menu_tambahan")} data-testid="add-addon-btn"
            className="gap-2 border-brand-line text-brand-espresso hover:bg-brand-surface transition-colors duration-200">
            <Plus className="h-4 w-4" /> Tambah Add On
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {addons.length === 0 && <div className="col-span-full rounded-2xl border border-dashed border-brand-line bg-white p-8 text-center text-brand-muted">Belum ada add on.</div>}
        {addons.map((p, i) => <Card key={p.id} p={p} i={i} />)}
      </div>

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editing ? "Ubah" : "Tambah"} {isBox ? "Nasi Box" : isAddon ? "Add On" : "Domba"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {isBox ? (
              <>
                <div>
                  <Label>Nama Nasi Box</Label>
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="package-name-input" className="mt-1.5 focus-visible:ring-brand-gold" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tipe Box</Label>
                    <Select value={form.box_tier} onValueChange={(v) => set("box_tier", v)}>
                      <SelectTrigger className="mt-1.5" data-testid="package-box-tier"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hemat">Hemat</SelectItem>
                        <SelectItem value="standar">Standar</SelectItem>
                        <SelectItem value="spesial">Spesial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Harga per Box (Rp)</Label>
                    <Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="package-price-input" className="mt-1.5 focus-visible:ring-brand-gold" />
                  </div>
                </div>
                <div>
                  <Label>Harga Dasar / Modal per Box (Rp)</Label>
                  <Input type="number" value={form.cost_price} onChange={(e) => set("cost_price", e.target.value)} data-testid="package-cost-input" className="mt-1.5 focus-visible:ring-brand-gold" />
                </div>
              </>
            ) : isAddon ? (
              <>
                <div>
                  <Label>Nama Add On</Label>
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="mis. Air Mineral, Kambing Guling" data-testid="package-name-input" className="mt-1.5 focus-visible:ring-brand-gold" />
                </div>
                <div>
                  <Label>Harga (Rp)</Label>
                  <Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="package-price-input" className="mt-1.5 focus-visible:ring-brand-gold" />
                </div>
                <div>
                  <Label>Harga Dasar / Modal (Rp)</Label>
                  <Input type="number" value={form.cost_price} onChange={(e) => set("cost_price", e.target.value)} data-testid="package-cost-input" className="mt-1.5 focus-visible:ring-brand-gold" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label>Nama/Tipe Domba</Label>
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="mis. Domba Tipe A" data-testid="package-name-input" className="mt-1.5 focus-visible:ring-brand-gold" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Harga Jual Mentah — Jantan (Rp)</Label>
                    <Input type="number" value={form.price_male} onChange={(e) => set("price_male", e.target.value)} data-testid="package-price-male" className="mt-1.5 focus-visible:ring-brand-gold" />
                  </div>
                  <div>
                    <Label>Harga Jual Mentah — Betina (Rp)</Label>
                    <Input type="number" value={form.price_female} onChange={(e) => set("price_female", e.target.value)} data-testid="package-price-female" className="mt-1.5 focus-visible:ring-brand-gold" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Harga Jual Masak — Jantan (Rp)</Label>
                    <Input type="number" value={form.price_male_cooked} onChange={(e) => set("price_male_cooked", e.target.value)} data-testid="package-price-male-cooked" className="mt-1.5 focus-visible:ring-brand-gold" />
                  </div>
                  <div>
                    <Label>Harga Jual Masak — Betina (Rp)</Label>
                    <Input type="number" value={form.price_female_cooked} onChange={(e) => set("price_female_cooked", e.target.value)} data-testid="package-price-female-cooked" className="mt-1.5 focus-visible:ring-brand-gold" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Modal Jantan (Rp)</Label>
                    <Input type="number" value={form.cost_male} onChange={(e) => set("cost_male", e.target.value)} placeholder="harga dasar jantan" data-testid="package-cost-male" className="mt-1.5 focus-visible:ring-brand-gold" />
                  </div>
                  <div>
                    <Label>Modal Betina (Rp)</Label>
                    <Input type="number" value={form.cost_female} onChange={(e) => set("cost_female", e.target.value)} placeholder="harga dasar betina" data-testid="package-cost-female" className="mt-1.5 focus-visible:ring-brand-gold" />
                  </div>
                </div>
                <div>
                  <Label>Biaya Masak per Ekor (Rp)</Label>
                  <Input type="number" value={form.cook_cost} onChange={(e) => set("cook_cost", e.target.value)} data-testid="package-cook-input" className="mt-1.5 focus-visible:ring-brand-gold" />
                </div>
              </>
            )}

            <div>
              <Label>Deskripsi</Label>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="package-desc-input" className="mt-1.5 focus-visible:ring-brand-gold" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={busy || !form.name} data-testid="save-package-btn" className="bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
